import { useIntl } from 'react-intl';
import classNames from 'classnames';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    GA4ItemListName,
    trackEecProductClick,
    trackEecProductImpression,
    trackToggleWishlist,
    trackWishlistError,
    WishlistError,
    WishListEventType,
} from '@jsmdg/tracking';
import { useWishlist, WishlistErrorTypes } from '@jsmdg/wishlist-library';
import { type WishlistResponseError } from '@jsmdg/wishlist-library/dist/esm/client/types';
import { DiscountBadgeShape, ProductTile, useSnackbarContext } from '@jsmdg/yoshi';
import { NotificationVariant } from '@jsmdg/yoshi/dist/enums/notification';
import { PageType } from '../../../shared/enums/pageType';
import { ProductAttribute } from '../../../shared/enums/productAttribute';
import { ProductType } from '../../../shared/enums/productType';
import { type FragmentContext, type Price, type Product } from '../../../shared/types';
import { type TrackingListName } from '../../enums';
import { getProductTrackingData } from '../../helper/getProductTrackingData';
import { useImpressionTracking } from '../../hooks';
import { productTileMessages as messages } from '../../i18n';
import {
    onCloseServices,
    onServicesActionButtonClick,
    onToggleServices,
} from './productTileTrackingHelper';

type ProductTileWrapperComponent = {
    readonly product: Product;
    readonly position: number;
    readonly lazyLoad: boolean;
    readonly trackingListName?: TrackingListName;
    readonly isPortraitTile?: boolean;
    readonly isExpandableTile?: boolean;
    readonly wishlist?: string[];
    readonly onProductTileClick?: (product: Product, position: number) => void;
};

const ProductTileWrapper = ({
    isExpandableTile,
    isPortraitTile,
    lazyLoad,
    onProductTileClick,
    position,
    product,
    trackingListName,
    wishlist,
}: ProductTileWrapperComponent): JSX.Element | null => {
    const { locale, pageType } = useFragmentContext<FragmentContext>();
    const intl = useIntl();

    const {
        classifications,
        closestLocation,
        discountBadge,
        hasPriceRange,
        images,
        inPreview,
        isTestProduct,
        locations,
        originalPrice,
        participants,
        price,
        productId,
        productType,
        rating,
        services,
        title,
        url,
    } = product;
    const {
        customerState,
        initialWishlist,
        isMydays,
        wishlistEnabled = false,
    } = useFragmentContext<FragmentContext>();
    const { isInWishlist, toggleWishlistItem } = useWishlist(
        customerState,
        wishlist ?? initialWishlist,
    );
    const isOnWishlist = isInWishlist(productId);
    const { openSnackbar } = useSnackbarContext();

    const a11yWishlistButton = isOnWishlist
        ? intl.formatMessage(messages.screenReaderTextForWishlistRemove)
        : intl.formatMessage(messages.screenReaderTextForWishlistAdd);

    const productTrackingData = getProductTrackingData(
        product,
        position,
        pageType,
        trackingListName,
    );

    const onlineExperienceKeys = ['Landesweit', 'Weltweit'];

    const isOnlineExperience =
        classifications?.includes('LocationOnline') &&
        locations?.some(location => location.name && onlineExperienceKeys.includes(location.name));

    const hasClosestLocation = !!closestLocation && !isOnlineExperience;

    const setImpressionTrackedElement = useImpressionTracking(() =>
        trackEecProductImpression({
            ...productTrackingData,
            list: trackingListName,
        }),
    );

    const onProductClick = (): void => {
        trackEecProductClick({
            ...productTrackingData,
            list: trackingListName,
        });

        onProductTileClick?.(product, position);
    };

    const onWishlistButtonClick = async (): Promise<void> => {
        const eventType = isOnWishlist
            ? WishListEventType.RemoveFromWishlist
            : WishListEventType.AddToWishlist;
        const itemListName =
            pageType === PageType.Wishlist
                ? GA4ItemListName.LastSeen
                : GA4ItemListName.Recommendation;
        try {
            await toggleWishlistItem(productId);
            trackToggleWishlist({
                type: eventType,
                productId,
                productTitle: product.title,
                itemListName,
            });
        } catch (error) {
            if (
                (error as WishlistResponseError).response?.data?.message ===
                    WishlistErrorTypes.COUNT_EXCEEDED ||
                (error as Error).message === WishlistErrorTypes.COUNT_EXCEEDED
            ) {
                openSnackbar({
                    content: intl.formatMessage(messages.tooManyWishlistArticlesError),
                    variant: NotificationVariant.Error,
                    a11yTextCloseButton: intl.formatMessage(messages.close),
                });
                trackWishlistError(
                    WishlistError.MoreThan99Products,
                    intl.formatMessage(messages.tooManyWishlistArticlesError),
                );
            } else {
                logToSentry(error as Error);
            }
        }
    };

    const getLocationLabel = (): string | undefined => {
        if (!locations?.length) {
            return undefined;
        }

        if (hasClosestLocation) {
            return closestLocation.name;
        }

        if (isOnlineExperience) {
            return intl.formatMessage(messages.onlineExperienceLocation);
        }

        if (locations.length > 1) {
            return intl.formatMessage(messages.multipleLocations, {
                locationCount: locations.length,
            });
        }

        return locations[0].name;
    };

    const getParticipantsLabel = (): string => {
        if (participants?.min === undefined || participants?.max === undefined) {
            return '';
        }

        const { max, min } = participants;

        return intl.formatMessage(messages.participantsLabel, {
            participants: min === max ? max : `${min}-${max}`,
            maxCount: max,
        });
    };

    const renderPrice = (renderablePrice: Price | undefined): string | undefined => {
        if (!renderablePrice) {
            return undefined;
        }

        return intl.formatNumber(Number(renderablePrice.gross.amount), {
            style: 'currency',
            currency: renderablePrice.gross.currencyCode,
        });
    };

    const getPricePrefix = (): string | undefined => {
        if (productType !== ProductType.ValueVoucherProduct || !hasPriceRange) {
            return undefined;
        }

        return intl.formatMessage(messages.pricePrefix);
    };

    return (
        <ProductTile
            key={productId}
            name={title}
            price={renderPrice(price)}
            originalPrice={renderPrice(originalPrice)}
            pricePrefix={getPricePrefix()}
            images={images}
            url={url}
            locale={locale}
            closestLocation={hasClosestLocation ? closestLocation : undefined}
            locations={locations}
            attributes={
                [
                    inPreview && ProductAttribute.Preview,
                    isTestProduct && ProductAttribute.Test,
                ].filter(Boolean) as string[]
            }
            services={
                isExpandableTile
                    ? services && {
                          caption: intl.formatMessage(messages.servicesCaptionMessage),
                          body: services.body,
                          servicesActionButtonLabel: '',
                          a11yCloseServicesLabel: intl.formatMessage(
                              messages.a11yCloseServicesMessage,
                          ),
                          onCloseServices: () => onCloseServices(product.title),
                          onToggleServices: showServices =>
                              onToggleServices(showServices, product.title),
                          onServicesActionButtonClick: event =>
                              onServicesActionButtonClick(product.title, url, event),
                      }
                    : undefined
            }
            rating={rating}
            lazyLoad={lazyLoad}
            maxParticipants={participants?.max}
            isPortraitTile={isPortraitTile}
            ref={setImpressionTrackedElement}
            locationLabel={getLocationLabel()}
            participantsLabel={getParticipantsLabel()}
            a11yPdpRedirectLabel={intl.formatMessage(messages.a11YPdpRedirection)}
            onProductClick={onProductClick}
            discountBadge={
                discountBadge
                    ? {
                          ...discountBadge,
                          withHint: isMydays,
                          className: classNames({
                              'ml-0-5x mt-0-5x ml-xs-1x mt-xs-1x': isMydays,
                          }),
                          shape: isMydays ? DiscountBadgeShape.Pill : DiscountBadgeShape.Rectangle,
                      }
                    : undefined
            }
            showWishlistButton={wishlistEnabled}
            toggleWishlist={onWishlistButtonClick}
            a11yWishlistButton={a11yWishlistButton}
            isOnWishlist={isOnWishlist}
            a11yExperienceText={title}
            a11yLocationText=""
            a11yButtonDetail=""
            a11yTextRating=""
            a11yOriginalPrice=""
            a11yCurrentPrice=""
            a11yCloseLocationText=""
            a11yAmountOfParticipants=""
        />
    );
};

export { ProductTileWrapper };
export type { ProductTileWrapperComponent };
